import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProducts } from '@/hooks/useProducts';
import { useProductTranslation } from '@/hooks/useProductTranslation';
import type { ProductSummary } from '@/types/product';

const TAG_I18N_MAP: Record<string, string> = {
  '热销中': 'tag_hot_selling',
  '新品': 'tag_new',
  '瓦楞纸': 'tag_corrugated_paper',
  '限时特惠': 'tag_limited_offer',
  '爆款': 'tag_bestseller',
  '推荐': 'tag_recommended',
  '猫薄荷': 'tag_catnip',
  '剑麻': 'tag_sisal',
  '护角': 'tag_corner_protector',
  '爬架': 'tag_cat_tree',
  '环保': 'tag_eco_friendly',
};

const CATEGORY_I18N_MAP: Record<string, string> = {
  '瓦楞纸': 'category_corrugated',
  '剑麻': 'category_sisal',
  '综合': 'category_combo',
};

function FeaturedCard({ product }: { product: ProductSummary }) {
  const { t } = useTranslation();
  const { getProductName, getProductDescription } = useProductTranslation();
  const displayName = getProductName(product as any);
  const displayDesc = getProductDescription(product as any);

  const translateTag = (tag: string): string => {
    const key = TAG_I18N_MAP[tag];
    return key ? t(key) : tag;
  };

  const translateCategory = (category: string): string => {
    const key = CATEGORY_I18N_MAP[category];
    return key ? t(key) : category;
  };

  return (
    <Link
      to={`/products/${product.id}`}
      className="group bg-white rounded-2xl overflow-hidden border border-[#f0ebe4] hover:border-[#C8714A]/30 transition-all duration-300 cursor-pointer"
    >
      <div className="relative overflow-hidden bg-[#faf8f5] h-64">
        <img
          src={product.image}
          alt={displayName}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          {product.tags?.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 rounded-full bg-[#C8714A] text-white text-xs font-medium"
            >
              {translateTag(tag)}
            </span>
          ))}
        </div>
      </div>

      <div className="p-5">
        <div className="text-xs text-[#999] mb-1">{translateCategory(product.category)}</div>
        <h3 className="text-base font-semibold text-[#1a1a1a] mb-2 group-hover:text-[#C8714A] transition-colors">
          {displayName}
        </h3>
        <p className="text-sm text-[#888] leading-relaxed line-clamp-2 mb-4">
          {displayDesc}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-[#C8714A]">${product.price}</span>
            {product.original_price > product.price && (
              <span className="text-sm text-[#bbb] line-through">${product.original_price}</span>
            )}
          </div>
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#C8714A]/10 group-hover:bg-[#C8714A] transition-colors">
            <i className="ri-arrow-right-line text-[#C8714A] group-hover:text-white text-sm transition-colors"></i>
          </div>
        </div>
      </div>
    </Link>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-[#f0ebe4] animate-pulse">
      <div className="h-64 bg-[#f5f0eb]"></div>
      <div className="p-5 flex flex-col gap-3">
        <div className="h-3 w-12 bg-[#f0ebe4] rounded-full"></div>
        <div className="h-5 w-3/4 bg-[#f0ebe4] rounded"></div>
        <div className="h-4 w-full bg-[#f0ebe4] rounded"></div>
        <div className="h-4 w-2/3 bg-[#f0ebe4] rounded"></div>
        <div className="flex justify-between items-center mt-1">
          <div className="h-6 w-14 bg-[#f0ebe4] rounded"></div>
          <div className="w-8 h-8 rounded-full bg-[#f0ebe4]"></div>
        </div>
      </div>
    </div>
  );
}

export default function FeaturedProducts() {
  const { t } = useTranslation();
  const { products, loading } = useProducts({ featuredOnly: true });

  return (
    <section className="py-20 md:py-28 px-6 md:px-10 bg-[#faf8f5]">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="text-[#C8714A] text-xs font-semibold tracking-widest uppercase mb-3">
              {t('featured_label')}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#1a1a1a] leading-tight">
              {t('featured_title')}
              <br />
              <span className="text-[#888] font-normal text-2xl md:text-3xl">{t('featured_subtitle')}</span>
            </h2>
          </div>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#C8714A] hover:text-[#b5603b] transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('featured_view_all')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
          {loading
            ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
            : products.map((product) => (
                <FeaturedCard key={product.id} product={product} />
              ))}
        </div>

        {!loading && products.length === 0 && (
          <div className="text-center py-16 text-[#bbb] text-sm">
            {t('featured_empty')}
          </div>
        )}
      </div>
    </section>
  );
}
